import { useEffect, useRef, useState } from "react";

const RING_LIFETIME = 420; // ms, longest ring (teal 350ms + 35ms delay) plus margin
const ACTIVATABLE = "button, a[href], summary, [role='button']";

function reduceMotion() {
  return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * One application level interaction layer.
 * Every intentional pointerdown anywhere on the page (empty background,
 * sections, cards, controls) spawns a coral ring followed by a teal ring at
 * the exact viewport coordinate. Keyboard activation (Enter or Space) of a
 * focused control spawns the same effect from the center of that control.
 * The layer is position: fixed with pointer-events: none so it never
 * intercepts input, and it never calls preventDefault or stopPropagation.
 */
export default function GlobalPointerEffects() {
  const [ripples, setRipples] = useState([]);
  const nextId = useRef(0);
  const timers = useRef(new Set());

  useEffect(() => {
    const timeouts = timers.current;

    const spawn = (x, y) => {
      if (reduceMotion()) return;
      const id = nextId.current++;
      setRipples((list) => [...list, { id, x, y }]);
      const t = setTimeout(() => {
        setRipples((list) => list.filter((r) => r.id !== id));
        timeouts.delete(t);
      }, RING_LIFETIME);
      timeouts.add(t);
    };

    const onPointerDown = (e) => {
      if (!e.isPrimary || (e.button !== undefined && e.button !== 0)) return;
      spawn(e.clientX, e.clientY);
    };

    const onKeyDown = (e) => {
      if (e.repeat || (e.key !== "Enter" && e.key !== " ")) return;
      const el = document.activeElement;
      if (!el || !el.matches(ACTIVATABLE)) return;
      const rect = el.getBoundingClientRect();
      spawn(rect.left + rect.width / 2, rect.top + rect.height / 2);
    };

    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  return (
    <div className="symp-pointer-layer" aria-hidden="true" data-testid="pointer-effect-layer">
      {ripples.map(({ id, x, y }) => (
        <span
          key={id}
          className="symp-pointer-effect"
          style={{ left: `${x}px`, top: `${y}px` }}
          data-testid="pointer-effect"
        >
          <span className="symp-ring symp-ring--coral" />
          <span className="symp-ring symp-ring--teal" />
          <span className={`symp-ring-dot${id % 2 ? " symp-ring-dot--teal" : ""}`} />
        </span>
      ))}
    </div>
  );
}
